import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import FormItem from 'components/form/FormItem';
import { FormFooter } from 'components/form/FormLayout.styled';
import * as F from 'components/form/FormLayout.styled';
import FormHeader from 'components/global/FormHeader';
import { HeaderContainerFormHeader } from 'components/global/styled/FormHeader.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import { getConnectedPartnersList } from 'pages/ShippingPartners/ConnectedPartners/ConnectedPartnersSlice.list';

import { ENUMS, OTOFLEX } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import PATHS, { mergePaths } from 'utils/paths';

import { faArrowLeft, faFloppyDisk } from '@fortawesome/pro-solid-svg-icons';

import * as S from './OTOFlexSettings.styled';

const OTOFlex = () => {
	const axios = useAxiosPrivate();
	const [isLoading, setIsLoading] = useState(false);
	const { showSuccess, showError } = useAlert();
	const { state } = useLocation();

	const dispatch = useDispatch();

	// schema of form
	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					connectionName: yup.string().nullable().required(),
					driverAssignmentMethod: yup.string().nullable().required(),
					proofOfPickup: yup
						.string()
						.nullable()
						.when('forceProofPickup', {
							is: true,
							then: (schema) => schema.required()
						}),
					forceProofPickup: yup.boolean(),
					proofOfDelivery: yup
						.string()
						.nullable()
						.when('forceProofDelivery', {
							is: true,
							then: (schema) => schema.required()
						}),
					forceProofDelivery: yup.boolean(),
					forceDriverDeliveryNote: yup.boolean(),
					googleApiKeyIpBased: yup.string().nullable(),
					googleApiKeyHttpBased: yup.string().nullable(),
					fleetName: yup.string().nullable().required(),
					deliverySLA: yup.number().nullable(),
					pickupSLA: yup.number().nullable(),
					notSendSMS: yup.boolean(),
					activate: yup.boolean()
				})
				.required(),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			fleetName: null,
			driverAssignmentMethod: 'nearestDriver',
			proofOfPickup: null,
			forceProofPickup: false,
			proofOfDelivery: null,
			forceProofDelivery: false,
			forceDriverDeliveryNote: false,
			googleApiKeyIpBased: null,
			googleApiKeyHttpBased: null,
			connectionName: null,
			deliverySLA: null,
			pickupSLA: null,
			notSendSMS: false,
			activate: false
		}
	});

	const {
		handleSubmit,
		setValue,
		watch,
		reset,
		formState: { isDirty, isSubmitting, isSubmitSuccessful }
	} = formMethods;

	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);

	const resetForm = (data) => {
		reset({
			fleetName: data.fleetName ?? null,
			driverAssignmentMethod: data.driverAssignmentMethod ?? 'nearestDriver',
			proofOfPickup: data.proofOfPickup ?? null,
			forceProofPickup: data.forceProofPickup ?? false,
			proofOfDelivery: data.proofOfDelivery ?? null,
			forceProofDelivery: data.forceProofDelivery ?? false,
			forceDriverDeliveryNote: data.forceDriverDeliveryNote ?? false,
			googleApiKeyIPBASED: data.googleApiKeyIPBASED ?? null,
			googleApiKeyHttpBASED: data.googleApiKeyHttpBASED ?? null,
			connectionName: data.connectionName ?? null,
			deliverySLA: data.deliverySLA ?? null,
			pickupSLA: data.pickupSLA ?? null,
			notSendSMS: data.notSendSMS ?? false,
			activate: data.activate ?? false
		});
	};

	const getOTOFlex = async () => {
		setIsLoading(true);
		try {
			const response = await axios.post(OTOFLEX.GET_OTO_FLEX_SETTINGS);
			const { data, status } = response;
			if (status === 200) {
				resetForm(data);
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		getOTOFlex();
	}, []);

	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);

	const onSubmit = async (data) => {
		setIsLoading(true);
		try {
			const response = await axios.post(OTOFLEX.UPDATE_OTO_FLEX_SETTINGS, {
				fleetName: data.fleetName,
				driverAssignmentMethod: data.driverAssignmentMethod,
				proofOfPickup: data.proofOfPickup,
				forceProofPickup: data.forceProofPickup,
				proofOfDelivery: data.proofOfDelivery,
				forceProofDelivery: data.forceProofDelivery,
				forceDriverDeliveryNote: data.forceDriverDeliveryNote,
				googleApiKeyIPBASED: data.googleApiKeyIPBASED,
				googleApiKeyHttpBASED: data.googleApiKeyHttpBASED,
				connectionName: data.connectionName,
				deliverySLA: data.deliverySLA,
				pickupSLA: data.pickupSLA,
				notSendSMS: data.notSendSMS,
				activate: data.activate
			});
			if (response.status === 200) {
				resetForm(data);
				showSuccess();
				await dispatch(getConnectedPartnersList({ invalidate: true })).unwrap();
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		} finally {
			setIsLoading(false);
		}
	};

	const colSpan = {
		lg: 8,
		xl: 7
	};

	const currentPlanName = useSelector((state) => state.user.planName);
	return (
		<div>
			<HeaderContainerFormHeader className="settingsMaxWidth settingsFormHeader">
				<FormHeader
					boldTitle="otoFlex"
					{...(state?.showBackButton
						? {
								lightTitle: state?.otoModule ? 'backToOTOFlexModule' : 'backToOTOFlexOrderPage',
								icon: faArrowLeft,
								backUrl: state?.otoModule
									? PATHS.OTO_FLEX_MODULE
									: mergePaths(PATHS.OTO_FLEX_MAIN, PATHS.UNASSIGNED_ORDERS)
							}
						: {})}
				/>
				<S.ButtonContainer>
					<Button
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/save-icon.json.gz"
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
						disabled={!isDirty || isLoading}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						icon={faFloppyDisk}
						label="save"
						variant="blue"
						className="saveButton"
					/>
				</S.ButtonContainer>
			</HeaderContainerFormHeader>

			<S.StyledForm formMethods={formMethods} borderless>
				<S.StyledCard className="settingsMaxWidth" fullWidth>
					{/* configure oto flex */}
					<FormItem colSpan={colSpan} label="connectionName" isLoading={isLoading} required>
						<StringInput name="connectionName" />
					</FormItem>
					<F.Line className="line" />
					<FormItem
						colSpan={colSpan}
						label="activate"
						isLoading={isLoading}
						description="tooltip.activate"
						direction="row-reverse">
						<SwitchInput name="activate" />
					</FormItem>
					<F.Line className="line" />
					<FormItem
						colSpan={colSpan}
						label="donotsendSMS"
						isLoading={isLoading}
						description="tooltip.donotSendSMS"
						direction="row-reverse">
						<SwitchInput name="notSendSMS" />
					</FormItem>
					{/* fleetName */}
					<F.Line className="line" />
					<FormItem
						colSpan={colSpan}
						label="fleetName"
						description="fleetName.description"
						isLoading={isLoading}
						required>
						<StringInput name="fleetName" />
					</FormItem>
					{/* fleetName */}
					<F.Line className="line" />
					{/* driverAssignmentMethod */}
					<FormItem
						colSpan={colSpan}
						label="driverAssignmentMethod"
						description="driverAssignmentMethod.description"
						isLoading={isLoading}
						required>
						<EnumSelect
							searchable={false}
							name="driverAssignmentMethod"
							enumName={
								currentPlanName !== 'EnterprisePackage'
									? ENUMS.DRIVER_ASSIGNMENT_METHOD_STARTER_SCALE
									: ENUMS.DRIVER_ASSIGNMENT_METHOD
							}
							placeholder="selectOne"
							valueRemovable={false}
							value={watch('driverAssignmentMethod')}
							setValue={(value) => {
								setValue('driverAssignmentMethod', value, { shouldValidate: true, shouldDirty: true });
							}}
						/>
					</FormItem>
					{/* driverAssignmentMethod */}
					<F.Line className="line" />
					{/* proofOfPickup */}
					<FormItem colSpan={colSpan} label="proofOfPickup" isLoading={isLoading}>
						<EnumSelect
							name="proofOfPickup"
							enumName={ENUMS.PROOF_OF_PICKUP_TYPE}
							placeholder="selectOne"
							value={watch('proofOfPickup')}
							setValue={(value) => {
								setValue('proofOfPickup', value, { shouldValidate: true, shouldDirty: true });
							}}
						/>
					</FormItem>
					{/* proofOfPickup */}
					<F.Line className="line" />
					{/* forceProofPickup */}
					<FormItem
						colSpan={colSpan}
						label="forceProofPickup"
						description="forceProofPickup.description"
						direction="row-reverse"
						isLoading={isLoading}>
						<SwitchInput name="forceProofPickup" />
					</FormItem>
					{/* forceProofPickup */}
					<F.Line className="line" />
					{/* proofOfDelivery */}
					<FormItem colSpan={colSpan} label="proofOfDelivery" isLoading={isLoading}>
						<EnumSelect
							name="proofOfDelivery"
							enumName={ENUMS.PROOF_OF_DELIVERY_TYPE}
							placeholder="selectOne"
							classNameChild="mt-1"
							value={watch('proofOfDelivery')}
							setValue={(value) => {
								setValue('proofOfDelivery', value, { shouldValidate: true, shouldDirty: true });
							}}
						/>
					</FormItem>
					{/* proofOfDelivery */}
					<F.Line className="line" />
					{/* forceProofDelivery */}
					<FormItem
						colSpan={colSpan}
						label="forceProofDelivery"
						description="forceProofDelivery.description"
						direction="row-reverse"
						isLoading={isLoading}>
						<SwitchInput name="forceProofDelivery" />
					</FormItem>
					{/* forceProofDelivery */}
					<F.Line className="line" />
					{/* forceDriverDeliveryNote */}
					<FormItem
						colSpan={colSpan}
						label="forceDriverDeliveryNote"
						description="forceDriverDeliveryNote.description"
						direction="row-reverse"
						isLoading={isLoading}>
						<SwitchInput name="forceDriverDeliveryNote" />
					</FormItem>
					{/* forceDriverDeliveryNote */}
					<F.Line className="line" />
					{/* googleApiKeyIPBASED */}
					<FormItem
						colSpan={colSpan}
						label="googleApiKeyIPBASED"
						description="googleApiKey.description.v2"
						isLoading={isLoading}>
						<StringInput name="googleApiKeyIPBASED" />
					</FormItem>
					{/* googleApiKeyIPBASED */}
					<F.Line className="line" />
					{/* googleApiKeyHttpBASED */}
					<FormItem
						colSpan={colSpan}
						label="googleApiKeyHttpBASED"
						description="googleApiKey.description.v2"
						isLoading={isLoading}>
						<StringInput name="googleApiKeyHttpBASED" />
					</FormItem>
					{/* googleApiKeyHttpBASED */}
					<F.Line className="line" />
					<FormItem
						colSpan={colSpan}
						label="deliverySLA"
						isLoading={isLoading}
						description="tooltip.deliverySla">
						<IntegerInput name="deliverySLA" unit="hrs" showArrows={false} />
					</FormItem>
					<F.Line className="line" />
					<FormItem colSpan={colSpan} label="pickupSLA" isLoading={isLoading} description="tooltip.pickupSla">
						<IntegerInput name="pickupSLA" unit="hrs" showArrows={false} />
					</FormItem>
					{/* configure oto flex */}
				</S.StyledCard>
			</S.StyledForm>
			<FormFooter>
				<Button
					lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/save-icon.json.gz"
					className="saveButton"
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					disabled={!isDirty || isLoading}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					icon={faFloppyDisk}
					label="save"
					variant="blue"
				/>
			</FormFooter>
		</div>
	);
};

export default OTOFlex;
